import { Button, InputAdornment, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import {
  ContactPage,
  ContactMap,
  ContactAdress,
  ContactWrite,
  ContactHead,
  ContactBody,
  ContactPhone,
  FaPhoneSquareAlt,
  FaViber,
  FaWhatsapp,
  ContactMail,
  WriteHeader,
  ContactHeader,
  MapPlaceholder,
  PrivacyNotice,
  FormStatus,
} from './ContactElements';

import { Message, AccountBox, Announcement, AlternateEmail } from '@mui/icons-material';

import { MainPageText } from '../../Library/LibraryMainPage';
import { useLanguage } from '../../../hooks';
import { Link } from 'react-router-dom';
import { usePrivacyConsent } from '../../../privacy/PrivacyConsent';

const privacyCopy = {
  ru: {
    mapText:
      'Карта Google передаёт Google технические данные, включая IP-адрес. Она загружается только с вашего разрешения.',
    loadMap: 'Загрузить карту Google',
    notice:
      'Мы используем эти данные только для ответа на ваш запрос. Обязательные поля нужны для обработки сообщения. Подробнее:',
    policy: 'политика конфиденциальности',
    sending: 'Отправка…',
    success: 'Сообщение отправлено. Мы свяжемся с вами в ближайшее время.',
    error:
      'Не удалось отправить сообщение. Повторите попытку или напишите на info@cogeneration.md.',
  },
  md: {
    mapText:
      'Harta Google transmite către Google date tehnice, inclusiv adresa IP. Se încarcă numai cu permisiunea dvs.',
    loadMap: 'Încarcă harta Google',
    notice:
      'Folosim aceste date numai pentru a răspunde solicitării. Câmpurile obligatorii sunt necesare pentru procesarea mesajului. Detalii:',
    policy: 'politica de confidențialitate',
    sending: 'Se trimite…',
    success: 'Mesajul a fost trimis. Vă vom contacta în curând.',
    error: 'Mesajul nu a putut fi trimis. Încercați din nou sau scrieți la info@cogeneration.md.',
  },
  en: {
    mapText:
      'Google Maps sends technical data, including your IP address, to Google. It loads only with your permission.',
    loadMap: 'Load Google map',
    notice:
      'We use this data only to answer your enquiry. Required fields are necessary to process the message. Details:',
    policy: 'privacy policy',
    sending: 'Sending…',
    success: 'Your message was sent. We will contact you shortly.',
    error: 'The message could not be sent. Please try again or email info@cogeneration.md.',
  },
};

const textFieldSx = {
  margin: 0,
  width: '100%',
  color: 'white',
  '& Mui-focused': {
    color: 'white',
  },
  '& label': {
    color: 'white',
    '&.Mui-focused': {
      color: 'white',
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'white' },
    '&:hover fieldset': { borderColor: 'white' },
    '&.Mui-focused fieldset': { borderColor: '#d3d3d3' },
  },
  '& .MuiInputBase-input, & input, & textarea': {
    color: 'white',
    WebkitTextFillColor: 'white',
    caretColor: 'white',
    '&::placeholder': {
      color: 'white',
      opacity: 0.75,
    },
  },
  '& .MuiSvgIcon-root': {
    color: '#d3d3d3',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & textarea:-webkit-autofill, & textarea:-webkit-autofill:hover, & textarea:-webkit-autofill:focus':
    {
      WebkitBoxShadow: '0 0 0 1000px #282828 inset',
      WebkitTextFillColor: 'white',
      caretColor: 'white',
    },
  '& input:autofill, & textarea:autofill': {
    boxShadow: '0 0 0 1000px #282828 inset',
    color: 'white',
    caretColor: 'white',
  },
};

const ContactSection = () => {
  const { lang } = useLanguage();
  const text = privacyCopy[lang] || privacyCopy.md;
  const { preferences, savePreferences } = usePrivacyConsent();
  const [values, setValues] = useState({
    mail: '',
    name: '',
    theme: '',
    message: '',
    website: '',
  });
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const resetForm = () => {
    setValues({
      name: '',
      mail: '',
      theme: '',
      message: '',
      website: '',
    });
    setStartedAt(Date.now());
  };

  const formSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: 'sending', message: text.sending });

    const data = {
      name: values.name,
      mail: values.mail,
      theme: values.theme,
      message: values.message,
      website: values.website,
      startedAt,
    };

    try {
      const response = await fetch('/api/back.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ data }),
      });
      if (!response.ok) throw new Error('Request failed');
      resetForm();
      setStatus({ type: 'success', message: text.success });
    } catch {
      setStatus({ type: 'error', message: text.error });
    }
  };

  return (
    <ContactPage id="contact">
      <ContactHeader>{MainPageText[lang].contacts}</ContactHeader>
      <ContactMap>
        {preferences.maps ? (
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d679.6166083495561!2d28.86174182920905!3d47.05069991246004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDAzJzAyLjUiTiAyOMKwNTEnNDQuMiJF!5e0!3m2!1sru!2s!4v1620285171855!5m2!1sru!2s"
            width="100%"
            height="300px"
            title="Google Maps"
            loading="lazy"
            referrerPolicy="no-referrer"
          ></iframe>
        ) : (
          <MapPlaceholder>
            <p>{text.mapText}</p>
            <button onClick={() => savePreferences({ ...preferences, maps: true })}>
              {text.loadMap}
            </button>
          </MapPlaceholder>
        )}
      </ContactMap>
      <ContactAdress>
        <ContactHead>{MainPageText[lang].contactArea}</ContactHead>
        <ContactBody>{MainPageText[lang].contactAdress}</ContactBody>
        <ContactPhone href="tel:+37369127963">
          <FaPhoneSquareAlt />
          &nbsp; +37369127963
        </ContactPhone>
        <ContactPhone href="https://api.whatsapp.com/send?phone=37369127963">
          <FaWhatsapp />
          &nbsp; Whatsapp
        </ContactPhone>
        <ContactPhone href="viber://chat/?number=%2B37369127963">
          <FaViber />
          &nbsp; Viber
        </ContactPhone>

        <ContactMail href="mailto:info@cogeneration.md">
          <AlternateEmail />
          <span>info@cogeneration.md</span>
        </ContactMail>
      </ContactAdress>

      <ContactWrite>
        <WriteHeader>{MainPageText[lang].writeEmail}</WriteHeader>
        <form onSubmit={formSubmit} autoComplete="on" style={{ width: '100%' }}>
          <Stack
            spacing={2}
            direction="column"
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              position: 'relative',
            }}
          >
            <TextField
              id="email"
              name="email"
              size="small"
              label={MainPageText[lang].writeEmailAdress}
              type="email"
              required
              inputProps={{ maxLength: 254 }}
              value={values.mail}
              onChange={handleChange('mail')}
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <AlternateEmail />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="name"
              name="name"
              size="small"
              label={MainPageText[lang].writeEmailName}
              type="text"
              required
              inputProps={{ maxLength: 120 }}
              value={values.name}
              onChange={handleChange('name')}
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <AccountBox />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="theme"
              name="subject"
              size="small"
              label={MainPageText[lang].writeEmailTheme}
              required
              inputProps={{ maxLength: 160 }}
              value={values.theme}
              onChange={handleChange('theme')}
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Announcement />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="message"
              name="message"
              size="small"
              label={MainPageText[lang].writeEmailMessage}
              multiline
              required
              inputProps={{ maxLength: 5000 }}
              value={values.message}
              onChange={handleChange('message')}
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Message />
                  </InputAdornment>
                ),
              }}
            />

            <input
              type="text"
              name="website"
              value={values.website}
              onChange={handleChange('website')}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px' }}
            />

            <PrivacyNotice>
              {text.notice} <Link to="/privacy">{text.policy}</Link>.
            </PrivacyNotice>

            {status.message && (
              <FormStatus role="status" $error={status.type === 'error'}>
                {status.message}
              </FormStatus>
            )}

            <Button
              type="submit"
              sx={{ border: '1px solid #fff', color: '#fff' }}
              disabled={status.type === 'sending'}
            >
              {MainPageText[lang].writeEmailSend}
            </Button>
          </Stack>
        </form>
      </ContactWrite>
    </ContactPage>
  );
};

export default ContactSection;
